import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Web Audio API Sound Generator ───────────────────────────────────────────
const AudioContext = window.AudioContext || window.webkitAudioContext;
let audioCtx = null;

const getAudioContext = () => {
  if (!audioCtx || audioCtx.state === 'closed') {
    try {
      audioCtx = new AudioContext();
    } catch (e) {
      console.log('[Celebration] Web Audio API not supported');
    }
  }
  return audioCtx;
};

// Generate filtered noise that sounds like applause/crowd cheering
const createApplauseNode = (ctx, duration, volume = 0.18) => {
  // Create noise buffer
  const sampleRate = ctx.sampleRate;
  const bufferSize = sampleRate * duration;
  const buffer = ctx.createBuffer(2, bufferSize, sampleRate);
  
  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < bufferSize; i++) {
      // Mix white noise with amplitude modulation for "clapping" texture
      const t = i / sampleRate;
      // Random clap bursts at varying rates (simulates many hands clapping)
      const burstRate1 = 8 + Math.sin(t * 2.5) * 3;
      const burstRate2 = 12 + Math.cos(t * 1.8) * 4;
      const burstRate3 = 6 + Math.sin(t * 3.2) * 2;
      const burst = 
        0.4 * (0.5 + 0.5 * Math.sin(t * burstRate1 * Math.PI * 2)) *
        (0.5 + 0.5 * Math.cos(t * burstRate2 * Math.PI * 2)) +
        0.3 * (0.5 + 0.5 * Math.sin(t * burstRate3 * Math.PI * 2)) +
        0.3;
      data[i] = (Math.random() * 2 - 1) * burst;
    }
  }
  
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  
  // Band-pass filter to shape noise into applause frequency range
  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = 3000;
  bandpass.Q.value = 0.5;
  
  // High-pass to remove rumble
  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 500;
  highpass.Q.value = 0.3;
  
  // Volume envelope: fade in, sustain, fade out
  const gainNode = ctx.createGain();
  const now = ctx.currentTime;
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(volume, now + 0.3);        // fade in
  gainNode.gain.setValueAtTime(volume, now + duration - 1.5);       // sustain
  gainNode.gain.linearRampToValueAtTime(0, now + duration);         // fade out
  
  source.connect(bandpass);
  bandpass.connect(highpass);
  highpass.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  return { source, gainNode };
};

// Add melodic chime layer on top of applause
const playChimeLayer = (ctx, startDelay = 0, volume = 0.12) => {
  const now = ctx.currentTime + startDelay;
  const playNote = (freq, time, dur, gain, type = 'sine') => {
    const osc = ctx.createOscillator();
    const vol = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    vol.gain.setValueAtTime(0, time);
    vol.gain.linearRampToValueAtTime(gain, time + 0.05);
    vol.gain.exponentialRampToValueAtTime(0.001, time + dur);
    osc.connect(vol);
    vol.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + dur);
  };
  
  // Ascending triumphant arpeggio
  playNote(523.25, now, 0.4, volume);                    // C5
  playNote(659.25, now + 0.15, 0.4, volume);             // E5
  playNote(783.99, now + 0.3, 0.5, volume);              // G5
  playNote(1046.5, now + 0.5, 0.8, volume * 1.2);        // C6
  // Shimmer harmonics
  playNote(1318.5, now + 0.7, 0.6, volume * 0.5, 'triangle');  // E6
  playNote(1568.0, now + 0.9, 0.5, volume * 0.4, 'triangle');  // G6
  // Resolve
  playNote(2093.0, now + 1.2, 1.0, volume * 0.6);        // C7 high ring
};

// ~5 second subchapter celebration: applause + sparkle chime
const playSubchapterSound = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const duration = 5;
    const { source } = createApplauseNode(ctx, duration, 0.14);
    source.start(ctx.currentTime);
    source.stop(ctx.currentTime + duration);
    // Add bright chime on top
    playChimeLayer(ctx, 0.1, 0.10);
    console.log('[Celebration] Subchapter applause playing (~5s)');
  } catch (e) {
    console.log('[Celebration] Subchapter sound failed:', e.message);
  }
};

// ~5 second chapter celebration: louder applause + richer fanfare
const playChapterSound = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const duration = 5;
    const { source } = createApplauseNode(ctx, duration, 0.20);
    source.start(ctx.currentTime);
    source.stop(ctx.currentTime + duration);
    // Louder, richer chime
    playChimeLayer(ctx, 0.1, 0.14);
    // Second wave of chimes for extra richness
    playChimeLayer(ctx, 1.5, 0.08);
    console.log('[Celebration] Chapter applause playing (~5s)');
  } catch (e) {
    console.log('[Celebration] Chapter sound failed:', e.message);
  }
};

// ─── Sparkle Particles (Subchapter) ─────────────────────────────────────────
const SPARKLE_COLORS = ['#FFD700', '#FFA500', '#FF6347', '#87CEEB', '#98FB98', '#DDA0DD', '#F0E68C'];

const SparkleEffect = ({ onComplete }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Multiple waves of sparkles over ~5 seconds
    const newParticles = Array.from({ length: 35 }, (_, i) => ({
      id: i,
      x: 15 + Math.random() * 70,
      y: 10 + Math.random() * 50,
      size: 4 + Math.random() * 10,
      color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
      delay: Math.random() * 3.5, // stagger over 3.5s so last ones finish at ~5s
      duration: 0.8 + Math.random() * 0.7,
      rotation: Math.random() * 360,
    }));
    setParticles(newParticles);
    const timer = setTimeout(() => onComplete?.(), 5000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[200]" aria-hidden="true">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            animation: `sparkle-pop ${p.duration}s ease-out ${p.delay}s both`,
          }}
        >
          <svg width={p.size * 2} height={p.size * 2} viewBox="0 0 24 24" style={{ transform: `rotate(${p.rotation}deg)` }}>
            <path
              d="M12 0L14.5 8.5L24 12L14.5 15.5L12 24L9.5 15.5L0 12L9.5 8.5Z"
              fill={p.color}
              opacity="0.9"
            />
          </svg>
        </div>
      ))}
      <style>{`
        @keyframes sparkle-pop {
          0% { opacity: 0; transform: scale(0) translateY(0); }
          30% { opacity: 1; transform: scale(1.2) translateY(-10px); }
          70% { opacity: 0.8; transform: scale(1) translateY(-20px); }
          100% { opacity: 0; transform: scale(0.3) translateY(-40px); }
        }
      `}</style>
    </div>
  );
};

// ─── Confetti + Stars (Chapter) ─────────────────────────────────────────────
const CONFETTI_COLORS = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A8E6CF', '#FF8A5C', '#6C5CE7', '#FD79A8', '#00B894', '#FDCB6E', '#E17055'];

const ConfettiEffect = ({ onComplete }) => {
  const [pieces, setPieces] = useState([]);

  useEffect(() => {
    const newPieces = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      delay: Math.random() * 2.5,
      duration: 2 + Math.random() * 2,
      size: 6 + Math.random() * 6,
      rotation: Math.random() * 720 - 360,
      drift: (Math.random() - 0.5) * 80,
      isStar: i < 15, // first 15 are stars, rest are confetti rectangles
    }));
    setPieces(newPieces);
    const timer = setTimeout(() => onComplete?.(), 5500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[200] overflow-hidden" aria-hidden="true">
      {pieces.map(p => (
        <div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: '-5%',
            animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s both`,
            '--drift': `${p.drift}px`,
            '--rotation': `${p.rotation}deg`,
          }}
        >
          {p.isStar ? (
            <svg width={p.size * 1.5} height={p.size * 1.5} viewBox="0 0 24 24">
              <path
                d="M12 0L14.5 8.5L24 12L14.5 15.5L12 24L9.5 15.5L0 12L9.5 8.5Z"
                fill={p.color}
              />
            </svg>
          ) : (
            <div
              style={{
                width: p.size,
                height: p.size * 0.6,
                backgroundColor: p.color,
                borderRadius: '1px',
              }}
            />
          )}
        </div>
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(0) translateX(0) rotate(0deg) scale(0);
          }
          10% {
            opacity: 1;
            transform: translateY(5vh) translateX(calc(var(--drift) * 0.2)) rotate(calc(var(--rotation) * 0.2)) scale(1);
          }
          80% {
            opacity: 0.8;
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) translateX(var(--drift)) rotate(var(--rotation)) scale(0.5);
          }
        }
      `}</style>
    </div>
  );
};

// ─── Main Hook ──────────────────────────────────────────────────────────────
export const useCelebration = () => {
  const [showSparkle, setShowSparkle] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const triggeredRef = useRef(new Set());

  const triggerSubchapterCelebration = useCallback((sectionKey) => {
    // Prevent duplicate triggers for the same section
    const key = `sub_${sectionKey}`;
    if (triggeredRef.current.has(key)) return;
    triggeredRef.current.add(key);
    
    setShowSparkle(true);
    playSubchapterSound();
  }, []);

  const triggerChapterCelebration = useCallback((topicId) => {
    const key = `ch_${topicId}`;
    if (triggeredRef.current.has(key)) return;
    triggeredRef.current.add(key);

    setShowConfetti(true);
    playChapterSound();
  }, []);

  const resetCelebrations = useCallback(() => {
    triggeredRef.current.clear();
  }, []);

  const CelebrationOverlay = useCallback(() => (
    <>
      {showSparkle && <SparkleEffect onComplete={() => setShowSparkle(false)} />}
      {showConfetti && <ConfettiEffect onComplete={() => setShowConfetti(false)} />}
    </>
  ), [showSparkle, showConfetti]);

  return {
    triggerSubchapterCelebration,
    triggerChapterCelebration,
    resetCelebrations,
    CelebrationOverlay,
  };
};

export default useCelebration;
